import React from 'react';

import 'whatwg-fetch';

import { API, Logger } from 'aws-amplify';
import { getExecutionInputLog } from './useExecutionManager';

const logger = new Logger('useExecutionInputLog');

function reducer(state, action) {
  switch (action.type) {
    case 'LOGS':
      logger.debug(`EXECUTION INPUT LOG URLS: `, action.payload);
      return { 
        ...state,
        logs: action.payload,
      };
    case 'ERROR':
      return { 
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'APPEND':
      logger.info(`EXECUTION INPUT LOG APPEND: `, action.payload, state);
      return { 
        ...state,
        truncated: action.payload.truncated,
        logEvents: [...state.logEvents, ...action.payload.logEntries] 
      };
    case 'COMPLETE':
      return { 
        ...state,
        loading: false,
      };
    default:
      return state;
  }
}

// adapted from https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
async function* makeLogLineIterator(downloadUrl) {
  const utf8Decoder = new TextDecoder('utf-8');
  const response = await window.fetch(downloadUrl);
  logger.info('FETCH RESPONSE:',response);
  const reader = response.body.getReader();
  let { value: chunk, done: readerDone } = await reader.read();
  chunk = chunk ? utf8Decoder.decode(chunk) : '';

  const re = /\n|\r|\r\n/gm;
  let startIndex = 0;

  for (;;) {
    let result = re.exec(chunk);
    if (!result) {
      if (readerDone) {
        break;
      }
      let remainder = chunk.substr(startIndex);
      ({ value: chunk, done: readerDone } = await reader.read());
      chunk = remainder + (chunk ? utf8Decoder.decode(chunk) : '');
      startIndex = re.lastIndex = 0;
      continue;
    }
    yield chunk.substring(startIndex, result.index);
    startIndex = re.lastIndex;
  }
  if (startIndex < chunk.length) {
    // last line didn't end in a newline char
    yield chunk.substr(startIndex);
  }
}

async function loadLog(downloadUrl, limit = 10000) {
  const logEntries=[];
  let truncated=false;
  for await (let line of makeLogLineIterator(downloadUrl)) {
    if(logEntries.length<limit) {
      logEntries.push(parseLogLine(line));
    } else {
      truncated=true;
      break;
    }
  }
  return { logEntries, truncated };
}

function parseLogLine(line) {
  let logEntryDate = new Date(line.substring(0,20));
  return {
    timestamp: logEntryDate.getTime(),
    message: line.substring(23).trimStart(),
  };
}

export const useExecutionInputLog = (props) => {

  const { executionId, inputUri } = props;

  const [state, dispatch] = React.useReducer(reducer, { 
    logEvents: [], 
    loading: true,
    error: null,
    truncated: false,
    logs: null,
  });

  const fetchLogs = async (variables) => {
    
    let logs=null;
    let remainingLimit = variables.limit;
    
    try {
      const response = await getExecutionInputLog(variables.executionId, variables.inputUri);
      logs=response.data.Result.Logs;
      dispatch({ type: 'LOGS', payload: logs });
      logs.forEach((log) => {
        if(remainingLimit>0) {
          loadLog(log.DownloadUrl, remainingLimit).then((entries) => {
            dispatch( { type: 'APPEND', payload: entries } );
            remainingLimit=remainingLimit-entries.logEntries.length;
          });
        }
      });
      dispatch({ type: 'COMPLETE'});
    } catch(error) {
      dispatch({ type: 'ERROR', payload: error});
    }

  };

  React.useEffect(() => {
    const variables = {
      executionId,
      inputUri,
      limit: 10000,
    };

    fetchLogs(variables);

    return () => { 
      logger.debug("useExecutionInputLogS3 cleanup!");
    };

  }, [executionId, inputUri]);
  
  return { 
    logEvents: state.logEvents, 
    loading: state.loading, 
    error: state.error,
    truncated: state.truncated,
  };

};
